/**
 * Shopper-facing delivery arrival estimates.
 *
 * Two sources feed this:
 *   1. Uber Direct quotes (checkout) give an exact `dropoff_eta` ISO timestamp.
 *   2. Product pages have no dropoff address yet, so we approximate locally from
 *      the boutique's prep time and the distance to the shopper.
 *
 * Both are presented as a window rather than a single time — a point estimate
 * reads as a promise, and courier pickup alone swings by several minutes.
 */

/** Minutes added after the quoted ETA to form the upper bound of the window. */
export const ETA_WINDOW_MINUTES = 20;

/** Courier assignment plus travel to the boutique, when we have no live quote. */
const DISPATCH_BUFFER_MINUTES = 10;

/** Average door-to-door city speed, well below highway speed because of stops. */
const AVERAGE_CITY_MPH = 18;

/** Matches the `prepTimeEstimate` default in DEFAULT_ONBOARDING_DATA. */
const DEFAULT_PREP_MINUTES = 30;

export interface ArrivalWindow {
  start: Date;
  end: Date;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function clockTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** "PM" / "AM" for the given time, so we can drop the redundant one. */
function meridiem(d: Date): string {
  return d.getHours() < 12 ? 'AM' : 'PM';
}

/**
 * Turn a quoted ETA into a window. Returns null for missing or unparseable
 * input so callers can fall back to generic copy instead of rendering "Invalid
 * Date" — the raw ISO string leaking into the UI is exactly the bug this
 * module exists to prevent.
 */
export function arrivalWindowFromEta(
  iso: string | null | undefined,
  spreadMinutes: number = ETA_WINDOW_MINUTES
): ArrivalWindow | null {
  if (!iso) return null;
  const start = new Date(iso);
  if (isNaN(start.getTime())) return null;
  return { start, end: new Date(start.getTime() + spreadMinutes * 60_000) };
}

/**
 * Approximate arrival without a courier quote. `miles` may be null when we
 * can't place the shopper, in which case only prep and dispatch are counted.
 */
export function estimateArrivalWindow(opts: {
  prepMinutes?: number | null;
  miles?: number | null;
  now?: Date;
}): ArrivalWindow {
  const now = opts.now ?? new Date();
  const prep = opts.prepMinutes && opts.prepMinutes > 0 ? opts.prepMinutes : DEFAULT_PREP_MINUTES;

  // Dispatch runs while the boutique is packing, so these overlap rather than add.
  const untilPickup = Math.max(prep, DISPATCH_BUFFER_MINUTES);
  const driveMinutes =
    typeof opts.miles === 'number' && opts.miles > 0
      ? Math.round((opts.miles / AVERAGE_CITY_MPH) * 60)
      : 0;

  const start = new Date(now.getTime() + (untilPickup + driveMinutes) * 60_000);
  return { start, end: new Date(start.getTime() + ETA_WINDOW_MINUTES * 60_000) };
}

/**
 * Render a window as "4:05 - 4:25 PM". The meridiem is printed once when both
 * ends share it, and a day prefix is added whenever the arrival isn't today —
 * a late-evening order can land past midnight.
 */
export function formatArrivalWindow(window: ArrivalWindow | null, now: Date = new Date()): string | null {
  if (!window) return null;
  const { start, end } = window;
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const startLabel =
    meridiem(start) === meridiem(end)
      ? clockTime(start).replace(/\s*[AP]M$/i, '')
      : clockTime(start);

  const range = `${startLabel} - ${clockTime(end)}`;

  if (isSameDay(end, now)) return range;

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (isSameDay(end, tomorrow)) return `Tomorrow, ${range}`;

  return `${end.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}, ${range}`;
}

/** Convenience wrapper: quoted ETA straight to display text. */
export function formatEtaAsWindow(
  iso: string | null | undefined,
  spreadMinutes: number = ETA_WINDOW_MINUTES
): string | null {
  return formatArrivalWindow(arrivalWindowFromEta(iso, spreadMinutes));
}
